import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown, FolderPlus, FolderTree, Plus, Upload } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  categoriesQueryOptions,
  CATEGORY_CASH_FLOW_GROUPS,
  type CategoryCashFlowGroup,
} from '@/features/categories/api'
import { CategoryFormDrawer } from '@/features/categories/category-form-drawer'
import { CategoryRow } from '@/features/categories/category-row'
import type { CategoryOut } from '@/shared/api/types'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Popover } from '@/shared/ui/popover'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/configuracoes/categorias')({
  // Sem kind = plano de contas completo; a tela agrupa pelos grupos do DFC.
  loader: ({ context }) => context.queryClient.ensureQueryData(categoriesQueryOptions()),
  component: CategoriasPage,
})

function CategoriasPage() {
  const { t } = useTranslation(['categories', 'nav', 'common'])
  const [drawerAberto, setDrawerAberto] = useState(false)
  const [menuAberto, setMenuAberto] = useState(false)
  // null = criar; categoria = editar. Alimenta a `key` do drawer (form fresco).
  const [emEdicao, setEmEdicao] = useState<CategoryOut | null>(null)

  const { data } = useQuery(categoriesQueryOptions())
  const itens = useMemo(() => data?.items ?? [], [data])

  // Uma seção por GRUPO do fluxo de caixa (DFC), na ordem fixa da Nibo. Um grupo
  // pode ter entrada E saída — a natureza vira a seta ↑/↓ na linha, não a seção.
  const porGrupo = useMemo(() => {
    const map = new Map<CategoryCashFlowGroup, CategoryOut[]>()
    for (const g of CATEGORY_CASH_FLOW_GROUPS) map.set(g, [])
    for (const c of itens) map.get(c.cash_flow_group)?.push(c)
    for (const lista of map.values()) lista.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    return map
  }, [itens])

  const abrirCriar = () => {
    setEmEdicao(null)
    setMenuAberto(false)
    setDrawerAberto(true)
  }

  const itemMenu =
    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-surface-2'

  const menuAdicionar = (
    <Popover
      open={menuAberto}
      onOpenChange={setMenuAberto}
      align="end"
      ariaLabel={t('categories:addMenu.aria')}
      className="w-60 p-1"
      trigger={
        <Button variant="primary">
          <Plus aria-hidden />
          {t('categories:add')}
          <ChevronDown aria-hidden />
        </Button>
      }
    >
      <div className="flex flex-col">
        <button type="button" className={itemMenu} onClick={abrirCriar}>
          <FolderPlus className="text-ink-muted size-4 shrink-0" aria-hidden />
          {t('categories:addMenu.category')}
        </button>
        {/* Subgrupo (nível intermediário) vem na Fase 2 → "Em breve". */}
        <div
          className="text-ink-subtle flex w-full cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-left text-sm"
          aria-disabled
          title={t('common:comingSoon.title')}
        >
          <FolderTree className="size-4 shrink-0" aria-hidden />
          <span>{t('categories:addMenu.subgroup')}</span>
          <Badge tone="soon" className="ml-auto">
            {t('common:comingSoon.badge')}
          </Badge>
        </div>
        {/* Importar planilha: sem endpoint de importação → desabilitado "Em breve". */}
        <div
          className="text-ink-subtle flex w-full cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-left text-sm"
          aria-disabled
          title={t('common:comingSoon.title')}
        >
          <Upload className="size-4 shrink-0" aria-hidden />
          <span>{t('categories:addMenu.import')}</span>
          <Badge tone="soon" className="ml-auto">
            {t('common:comingSoon.badge')}
          </Badge>
        </div>
      </div>
    </Popover>
  )

  return (
    <>
      <PageHeader title={t('nav:settings.categories')} actions={menuAdicionar} />

      <div className="p-6">
        {itens.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title={t('categories:empty.title')}
            description={t('categories:empty.description')}
            action={
              <Button variant="primary" onClick={abrirCriar}>
                <Plus aria-hidden />
                {t('categories:addMenu.category')}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-6">
            {CATEGORY_CASH_FLOW_GROUPS.map((grupo) => {
              const lista = porGrupo.get(grupo) ?? []
              return (
                <section key={grupo}>
                  {/* Cabeçalho do grupo (banda cinza), como no Nibo. */}
                  <div className="bg-surface-2 text-ink-muted rounded-md px-3 py-2">
                    <span className="text-2xs font-semibold tracking-wide uppercase">
                      {t(
                        `categories:cashFlowGroups.${grupo}` as 'categories:cashFlowGroups.operating_revenue',
                      )}
                    </span>
                  </div>

                  {lista.length === 0 ? (
                    <p className="text-ink-muted px-3 py-3 text-sm">{t('categories:emptyGroup')}</p>
                  ) : (
                    <ul
                      aria-label={t(
                        `categories:cashFlowGroups.${grupo}` as 'categories:cashFlowGroups.operating_revenue',
                      )}
                      className="flex flex-col"
                    >
                      {lista.map((c) => (
                        <CategoryRow
                          key={c.id}
                          node={c}
                          onEdit={(cat) => {
                            setEmEdicao(cat)
                            setDrawerAberto(true)
                          }}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        )}
      </div>

      <CategoryFormDrawer
        // key: remonta o form ao alternar criar↔editar/entre categorias — carrega
        // os defaults certos (mesmo cuidado do bug "fornecedor vira cliente").
        key={emEdicao ? `edit-${emEdicao.id}` : 'new'}
        category={emEdicao ?? undefined}
        open={drawerAberto}
        onOpenChange={(aberto) => {
          setDrawerAberto(aberto)
          if (!aberto) setEmEdicao(null)
        }}
      />
    </>
  )
}
